import { Link } from "gatsby"
import React, { useContext } from "react"
import Layout from "../../components/layout"
import Seo from "../../components/seo"
import { StaticImage } from "gatsby-plugin-image"

import styled from "styled-components"

import WebriQForm from "@webriq/gatsby-webriq-form"

const Banner = () => {
  return (
    <>
      <div className="hero-new-study">
        <div className="container">
          <div className="row">
            <div className="col-md-12 mx-auto">
              <div className="title-banner">
                <h1 className="text-center">
                  Precedent Launches Custom Quickbase App with TalkDesk
                  Integration in Under 30 Days
                </h1>
                <div className="d-flex flex-column flex-sm-row justify-content-center justify-content-sm-start text-center text-sm-left text-white">
                  <p className="pr-sm-1 mb-1 mb-sm-0">
                    <strong>Summary:</strong>
                  </p>
                  <p>
                    Facing tight deadlines and a surge of new users, Precedent
                    partnered with Quandary Consulting Group to launch a custom
                    Quickbase app that integrated with Talkdesk to support
                    onboarding and customer support in under a month.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const LogoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 24px;
  padding: 24px 0;
`

const LogoCard = styled.div`
  background: #fff;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  min-width: 160px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  padding: 12px;
`

const LogoImg = styled.img`
  max-width: 100%;
  max-height: 48px;
  object-fit: contain;
`

const Custom = ({ location }) => {
  return (
    <>
      <Layout banner={<Banner />} location={location}>
        <Seo
          title="Custom Quickbase App Speeds Agent Onboarding"
          description="Precedent needed to onboard agents fast. Quandary delivered a scalable Quickbase app that simplified workflows and reduced training complexity."
        />
        <div className="py-4 py-md-5 case-studies-inner new-case-studies-inner bg-white">
          <div className="container">
            <div className="row">
              <div className="col-md-4 col-lg-4">
                <div
                  className="sidebar-tab relative results"
                  style={{ position: "sticky", top: "110px" }}
                >
                  <div className="relative results bg-case-study p-4">
                    <h3 className="mt-0 ">Results Snapshot</h3>
                    <ul className="ml-0">
                      <li>Onboarded agents faster with guided workflows</li>
                      <li>Reduced training time and confusion</li>
                      <li>Delivered role-specific guardrails</li>
                      <li>Freed up CEO to focus on growth</li>
                      <li>Created a foundation for scalable hiring</li>
                    </ul>
                  </div>
                  <a
                    className="btn btn-primary btn-arrow mr-2 mt-4 text-center relative w-full"
                    download
                    target="_blank"
                    href="https://cdn.sanity.io/files/55lcecww/production/0342d11871010b58ed5797e5e97080e9be4801da.pdf"
                  >
                    Download This Story
                  </a>
                </div>
              </div>
              <div className="col-md-8 col-lg-8">
                <section className="relative">
                  <div className="relative px-0">
                    <div className="row mx-0">
                      <div className="col-md-11 mx-auto p-4 content-wrapper">
                        <div className="client-content">
                          <h2>The Challenge</h2>
                          <p>
                            Precedent's CEO had built a strong Quickbase
                            solution in-house to manage insurance claim
                            workflows. However, with a growing customer base and
                            a tight deadline, he no longer had the capacity to
                            scale the app while leading the business.
                          </p>
                          <p>
                            The company needed to rapidly onboard new agents and
                            improve daily workflows without slowing down
                            momentum.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-grey">
                        <div className="client-content">
                          <h2>Our Solution</h2>
                          <p>
                            Quandary delivered{" "}
                            <Link to="/custom-quickbase-application-development">
                              a custom Quickbase application
                            </Link>{" "}
                            designed to support Precedent's onboarding goals and
                            fast-changing workflows.
                          </p>
                          <p>
                            The system replaced manual call logging with a
                            guided interface that only shows agents what's
                            relevant for each interaction, helping new hires
                            ramp up faster and stay focused.
                          </p>
                          <p>
                            The application also integrated Talkdesk, enabling
                            agents to make calls directly from Quickbase while
                            following a step-by-step workflow. Once a call ends,
                            the system records the outcome and advances the case
                            automatically.
                          </p>
                          <p>
                            The solution gave Precedent a scalable,
                            role-specific workflow that could keep pace with the
                            company's growth.
                          </p>
                          <p>
                            <strong>Key Features:</strong>
                          </p>
                          <ul className="pl-4">
                            <li>
                              Guided interface to reduce onboarding friction
                            </li>
                            <li>
                              Role-specific workflows to focus agents on
                              relevant tasks
                            </li>
                            <li>
                              Integrated Talkdesk calling within Quickbase
                            </li>
                            <li>Automated case advancement after each call</li>
                            <li>
                              Embedded PDF viewer to meet compliance standards
                            </li>
                            <li>
                              Secure, in-app display of case documentation
                            </li>
                            <li>Scalable structure to support future hiring</li>
                          </ul>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper">
                        <div className="client-content">
                          <h2>Our Process</h2>
                          <p>
                            Precedent needed a working application within a
                            month. With no time for a full discovery phase,{" "}
                            <Link to="/our-approach">
                              our team focused on gaining clarity quickly
                            </Link>{" "}
                            and adapting as the build progressed.
                          </p>
                          <p>
                            <strong>Key Steps:</strong>
                          </p>
                          <ol className="decimal">
                            <li>
                              <strong>Prioritized clarity early:</strong> Spent
                              the first third of the project working with
                              Precedent to define core requirements before
                              development began.
                            </li>
                            <li>
                              <strong>Reviewed source materials:</strong>{" "}
                              Evaluated documentation, workflows, and
                              integration points to build a clear foundation.
                            </li>
                            <li>
                              <strong>Built-in sprints:</strong> Moved into
                              development quickly using an agile approach that
                              allowed for real-time adjustments.
                            </li>
                            <li>
                              <strong>Adjusted as needs evolved:</strong>{" "}
                              Incorporated changes week by week as the client
                              refined their workflows and surfaced new
                              requirements.
                            </li>
                            <li>
                              <strong>Maintained tight feedback loops:</strong>{" "}
                              Kept communication clear and decisions fast to
                              ensure delivery stayed on track.
                            </li>
                          </ol>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-gradient">
                        <div className="client-content">
                          <h2 className="text-white">The Roadblocks</h2>
                          <p className="text-white">
                            The engagement surfaced several technical and
                            delivery challenges that required quick
                            decision-making and flexible execution. With no time
                            for a full discovery phase, our team worked closely
                            with Precedent to clarify evolving requirements as
                            the build progressed.
                          </p>
                          <p className="text-white">
                            The original Talkdesk integration failed in Chrome
                            due to limitations with the browser's PDF viewer. We
                            explored a fallback in Firefox, but that option
                            broke compatibility with TopDesk. To keep everything
                            in a single workflow, we engineered a custom
                            solution that enabled the integration in Chrome.
                          </p>
                          <p className="text-white">
                            TopDesk also lacked the embedded call-logging
                            features the client expected. After escalating the
                            issue with the vendor, Precedent secured access to a
                            beta capability that allowed us to deliver the
                            interaction they needed.
                          </p>
                          <p className="text-white">
                            Finally, Chrome's PDF viewer didn't meet the
                            client's compliance standards. We addressed this by
                            embedding a secure, custom PDF viewer directly
                            within Quickbase.
                          </p>
                          <blockquote
                            className="testimonial"
                            style={{
                              background: "#fff",
                              borderRadius: "24px",
                              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                              padding: "32px 24px 16px 24px",
                              maxWidth: "100%",
                              textAlign: "center",
                              position: "relative",
                            }}
                          >
                            <div className="row mb-3">
                              <div className="col-lg-8">
                                <p
                                  style={{
                                    color: "#3a3a3a",
                                    fontStyle: "italic",
                                    fontSize: "20px",
                                  }}
                                >
                                  "Working with experts isn't always easy. They
                                  tend to think their way is the best way.
                                  Quandary presents solutions in a way that they
                                  can be discussed and well thought out so that
                                  the right decision can be made."
                                </p>
                              </div>
                              <div className="col-lg-4 mt-4 mt-lg-0">
                                <div className="row">
                                  <div className="col-6 col-md-12">
                                    <div
                                      className="relative user-img mb-2"
                                      style={{
                                        backgroundImage:
                                          'url("https://cdn.sanity.io/images/55lcecww/production/098c370a1febcf8df1a7c194d8bcaa3003d0c521-800x800.jpg")',
                                      }}
                                    />{" "}
                                  </div>
                                  <div className="col-6 col-md-12">
                                    <div className="relative text-left user-info ml-2">
                                      <h3>Grant Little</h3>
                                      <p className="my-2 my-md-0">
                                        CEO Precedent
                                      </p>
                                      <a
                                        href="https://www.linkedin.com/in/grantlittle1/"
                                        target="_blank"
                                      >
                                        <svg
                                          fill="#00b5f4 "
                                          stroke-width="0"
                                          viewBox="0 0 448 512"
                                          height="2em"
                                          width="2em"
                                          xmlns="http://www.w3.org/2000/svg"
                                          style={{
                                            height: "2em; width: 1.2em",
                                          }}
                                        >
                                          <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"></path>
                                        </svg>
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </blockquote>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper">
                        <div className="client-content">
                          <h2>The Toolstack</h2>
                          <LogoGrid>
                            <LogoCard>
                              <LogoImg
                                src="https://cdn.sanity.io/images/55lcecww/production/78673169bfad856d32e060c5bf029890db184c60-261x101.png"
                                alt="Quickbase"
                              />
                            </LogoCard>
                            <LogoCard>
                              <LogoImg
                                src="https://cdn.sanity.io/images/55lcecww/production/f22b3aa91996d5961292a9029bf6085938b08007-2560x413.png"
                                alt="Talkdesk"
                              />
                            </LogoCard>
                          </LogoGrid>
                          <p>
                            The solution was built in Quickbase, which Precedent
                            was already using internally. Quandary integrated
                            Talkdesk to enable in-app calling and embedded
                            TopDesk to streamline call logging within the
                            workflow.
                          </p>
                          <p>
                            Our team also implemented a custom JavaScript
                            library to securely display PDFs inside the
                            application, ensuring agents could view documents
                            without switching tools.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-blue">
                        <div className="client-content">
                          <h2 className="text-white">The Results</h2>
                          <p className="text-white">
                            By taking over development, Quandary freed up
                            Precedent's CEO to focus on growing the business
                            instead of managing the application.
                          </p>
                          <p className="text-white">
                            The new Quickbase app also made it easier to train
                            new agents, with role-specific workflows that reduce
                            confusion and keep team members on task.
                          </p>
                          <p className="text-white">
                            While the goal wasn't to speed up individual tasks,
                            the system created a more focused environment that
                            could scale with the company's rapid growth.
                          </p>
                          <p>
                            <strong className="text-white">Key Results:</strong>
                          </p>
                          <ol className="decimal text-white">
                            <li className="text-white">
                              Reduced onboarding time with guided workflows
                            </li>
                            <li className="text-white">
                              Minimized training complexity for new agents
                            </li>
                            <li className="text-white">
                              Delivered a role-specific system to keep teams
                              focused
                            </li>
                            <li className="text-white">
                              Freed up the CEO to focus on growth
                            </li>
                            <li className="text-white">
                              Built a scalable foundation for future hiring
                            </li>
                          </ol>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper">
                        <div className="client-content">
                          <h2>What's Next?</h2>
                          <p>
                            After launch, Quandary supported Precedent in hiring
                            a senior in-house Quickbase developer. Our team is
                            leading knowledge transfer and onboarding to ensure
                            internal ownership of the platform.
                          </p>
                          <p>
                            As new needs emerge,{" "}
                            <Link to="/faq">
                              we continue to provide technical support
                            </Link>{" "}
                            and contribute to future enhancements.
                          </p>
                          <p>
                            With this foundation in place, Precedent is
                            positioned to move faster and scale without
                            reinventing core systems.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-grey">
                        <div className="client-content">
                          <h2>Client Overview:</h2>
                          <p>
                            <Link to="https://precedent.com/" target="_blank">
                              Precedent
                            </Link>{" "}
                            is a SaaS company that helps law firms and insurance
                            providers streamline claim processing. Their
                            platform reduces administrative complexity and
                            improves coordination between parties.
                          </p>
                          <div className="row align-items-center">
                            <div className="col-md-6 pr-md-4">
                              <p>
                                <strong>Industry:</strong> LegalTech / InsurTech
                                <span className="relative d-block">
                                  <strong>Location:</strong> Charlotte, NC
                                </span>
                                <span className="relative d-block">
                                  <strong>Size:</strong> 50
                                </span>
                              </p>
                            </div>

                            <div className="col-md-6 pl-md-4 d-flex justify-content-center align-items-center border-left-md">
                              <LogoGrid>
                                <LogoCard>
                                  <LogoImg
                                    src="https://cdn.sanity.io/images/55lcecww/production/d4897c4eff3af5ae876b523c7a21bdfeffdd0e4c-1575x357.png"
                                    alt="Quickbase"
                                  />
                                </LogoCard>
                              </LogoGrid>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto px-0 pt-4 content-wrapper">
                        <div className="client-content">
                          <div id="speak-with-specialist">
                            <div className="form-wrap-contact">
                              <div className="form-wrapper">
                                <div className="intro mb-2">
                                  <div className="row">
                                    <div className="col-md-12">
                                      <h2 className="mt-0 font-weight-medium">
                                        Speak with Our Innovation Team
                                      </h2>
                                      <p>
                                        Use the form below to drop us an e-mail.
                                        We'll be in touch shortly.
                                      </p>
                                      <small className="text-red">
                                        Fields marked with an asterisk (*) are
                                        required.
                                      </small>
                                    </div>
                                  </div>
                                </div>
                                <WebriQForm
                                  id="contact-form"
                                  name="Contact Form"
                                  className="contactForm"
                                  data-form-id="4d46fe65-4d66-46b4-946c-e5826349ed3c"
                                  data-thankyou-url="/thank-you"
                                >
                                  <div className="row">
                                    <div className="col-lg-6 col-md-12 col-sm-12">
                                      {/* <p class="required">Full Name</p> */}

                                      <input
                                        type="text"
                                        name="Full Name"
                                        size={40}
                                        className="form-control required"
                                        placeholder="Full Name *"
                                        aria-label="Full Name"
                                        required
                                        id="requiredField"
                                      />
                                    </div>
                                    <div className="col-lg-6 col-md-12 col-sm-12 mt-4 mt-lg-0">
                                      {/* <p class="required">Phone</p> */}
                                      <input
                                        type="text"
                                        name="Phone"
                                        size={40}
                                        // minLength={14}
                                        className="form-control required"
                                        id="phone"
                                        placeholder="Phone Number *"
                                        aria-label="Phone"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">Email</p> */}
                                      <input
                                        type="email"
                                        name="Email"
                                        size={40}
                                        className="form-control"
                                        aria-label="Email"
                                        placeholder="Email *"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">Message</p> */}
                                      <textarea
                                        name="Message"
                                        rows={5}
                                        cols={0}
                                        className="form-control"
                                        aria-label="Message"
                                        placeholder="Message *"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">How did you find out about us?</p> */}
                                      <div>
                                        <textarea
                                          type="text"
                                          name="How did you find out about us?"
                                          size={40}
                                          rows={5}
                                          className="form-control"
                                          aria-label="How did you find out about us?"
                                          placeholder="How did you find out about us? *"
                                          required
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="row mt-3">
                                    <div className="form-group col-12 d-flex justify-content-start captcha-wrap">
                                      <div className="webriq-recaptcha" />
                                    </div>
                                    <div className="form-group col-12 text-left">
                                      <div className="input-filled">
                                        <button
                                          type="submit"
                                          className="btn btn-primary btn-arrow"
                                        >
                                          SUBMIT
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </WebriQForm>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  )
}

export default Custom
